import { message } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { AvatarType, MetaKey } from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'
import { showErrorMessage } from '@suntex.fim/ui'

import { DEFAULT_AVATAR } from '../constants/avatarConstants'
import type {
  AvatarItem,
  AvatarMetaData,
  UseAvatarReturn
} from '../types/avatar'
import {
  calculateCurrentLevel,
  findAvatarByName,
  getCurrentStickerCount,
  getDefaultAvatar,
  validateAvatarData
} from '../utils/avatarUtils'

export const useAvatar = (): UseAvatarReturn => {
  const { api } = useFramework()
  const [selectedColor, setSelectedColor] = useState<string>(
    DEFAULT_AVATAR.BACKGROUND_COLOR
  )
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoadedSavedAvatar, setHasLoadedSavedAvatar] = useState(false)
  const [lastSavedAvatarData, setLastSavedAvatarData] =
    useState<AvatarMetaData | null>(null)
  const { login } = useAuth(isAuthenticated)

  // Memoized calculations
  const currentStickerCount = useMemo(() => getCurrentStickerCount(), [])
  const currentLevel = useMemo(
    () => calculateCurrentLevel(currentStickerCount),
    [currentStickerCount]
  )

  // Initialize selected avatar with the correct default avatar based on user's progress
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarItem>(() =>
    getDefaultAvatar(currentLevel, currentStickerCount)
  )

  // Load user's saved avatar
  const loadAvatar = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await api.get_Login_LoginMeta({
        loginId: login.id,
        metaKey: MetaKey.PlayerAvatar
      })

      if (response.data && validateAvatarData(response.data)) {
        const data = response.data
        setSelectedColor(data.BgColor)
        setLastSavedAvatarData(data) // Store the saved data for comparison

        const foundAvatar = findAvatarByName(data.AvatarIcon)
        if (foundAvatar) {
          setSelectedAvatar(foundAvatar)
          setHasLoadedSavedAvatar(true)
        }
      } else {
        // No saved avatar data, use default
        setHasLoadedSavedAvatar(false)
        setLastSavedAvatarData(null)
        const defaultAvatar = getDefaultAvatar(
          currentLevel,
          currentStickerCount
        )
        setSelectedAvatar(defaultAvatar)
      }
    } catch (error) {
      showErrorMessage('Failed to load avatar.', error)
    } finally {
      setIsLoading(false)
    }
  }, [login.id])

  // Update selected avatar when level/stickers change (only if no saved avatar)
  useEffect(() => {
    if (!hasLoadedSavedAvatar) {
      const latestDefault = getDefaultAvatar(currentLevel, currentStickerCount)
      setSelectedAvatar(latestDefault)
    }
  }, [currentLevel, currentStickerCount, hasLoadedSavedAvatar])

  // Load avatar on component mount
  useEffect(() => {
    void loadAvatar()
  }, [loadAvatar])

  // Save avatar - wrapped to handle void return
  const handleSaveAvatar = useCallback(
    (type: AvatarType) => {
      const performSave = async (): Promise<void> => {
        try {
          setIsLoading(true)

          let data: AvatarMetaData
          if (type === AvatarType.Default) {
            const defaultAvatar = getDefaultAvatar(
              currentLevel,
              currentStickerCount
            )
            data = {
              AvatarIcon: defaultAvatar.name,
              BgColor: DEFAULT_AVATAR.BACKGROUND_COLOR
            }
          } else {
            data = {
              AvatarIcon: selectedAvatar.name,
              BgColor: selectedColor
            }
          }

          // Check if the data is the same as what's already saved
          if (
            lastSavedAvatarData &&
            lastSavedAvatarData.AvatarIcon === data.AvatarIcon &&
            lastSavedAvatarData.BgColor === data.BgColor
          ) {
            message.info('Avatar is already saved with the same settings.')
            return
          }

          await api.post_Login_UpsertLoginMeta({
            loginId: login.id,
            metaKey: MetaKey.PlayerAvatar,
            data
          })

          // Update local state and saved data reference
          if (type === AvatarType.Default) {
            const defaultAvatar = getDefaultAvatar(
              currentLevel,
              currentStickerCount
            )
            setSelectedAvatar(defaultAvatar)
            setSelectedColor(DEFAULT_AVATAR.BACKGROUND_COLOR)
          }

          setLastSavedAvatarData(data)
          setHasLoadedSavedAvatar(true)
          message.success('Player avatar saved successfully!')
        } catch (error) {
          showErrorMessage('Failed to save avatar.', error)
        } finally {
          setIsLoading(false)
        }
      }

      void performSave()
    },
    [
      login.id,
      selectedAvatar.name,
      selectedColor,
      currentLevel,
      currentStickerCount,
      lastSavedAvatarData
    ]
  )

  // Event handlers
  const handleColorSelect = useCallback((color: string) => {
    setSelectedColor(color)
  }, [])

  const handleAvatarSelect = useCallback((avatar: AvatarItem) => {
    setSelectedAvatar(avatar)
  }, [])

  return {
    selectedColor,
    selectedAvatar,
    isLoading,
    currentLevel,
    currentStickerCount,
    handleColorSelect,
    handleAvatarSelect,
    saveAvatar: handleSaveAvatar
  }
}
